import Controller from '@ember/controller';
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import ENV from 'lumen/config/environment';
import type { LookerDashboard } from 'lumen/models';
import type MarketCustomReportingDashboardRoute from 'lumen/routes/market/custom-reporting/dashboard';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { ModelFrom } from 'lumen/utils/routes';

type LookerMessageData = {
  canEdit: boolean;
  dashboard_filters: Record<string, unknown>;
  id: string;
  options: {
    elements: object;
    layouts: object[];
  };
};

type IFrameQueryParams = {
  embed_domain: string;
  market_id?: string;
  timezone: string;
  user_type: string;
};

export default class MarketCustomReportingDashboardController extends Controller {
  declare model: ModelFrom<MarketCustomReportingDashboardRoute>;

  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare customReporting: Services['custom-reporting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked canEdit = false;
  @tracked currentFilters: Record<string, unknown> = {};
  @tracked initialFilters: Record<string, unknown> = {};
  @tracked isEmpty = true;
  @tracked isRefreshRunning = false;
  @tracked lastRunAt?: Dayjs;
  @tracked lastRunAtFormatted?: string;

  intervalTimer?: ReturnType<typeof setInterval>;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editOptions() {
    if (this.isEmpty) {
      return [
        {
          id: 'delete',
          icon: 'bin',
          label: this.intl.t('custom_reporting.actions.delete'),
        },
      ] as const;
    }

    if (!this.canEdit || !this.access.hasCustomReportingEdit) {
      return [
        {
          id: 'schedule',
          icon: 'calendar-clock',
          label: this.intl.t('custom_reporting.schedule_report'),
        },
        {
          id: 'reset-filters',
          icon: 'undo',
          label: this.intl.t('custom_reporting.reset_page_filters'),
        },
      ] as const;
    }

    return [
      {
        id: 'schedule',
        icon: 'calendar-clock',
        label: this.intl.t('custom_reporting.schedule_report'),
      },
      {
        id: 'reset-filters',
        icon: 'undo',
        label: this.intl.t('custom_reporting.reset_page_filters'),
      },
      {
        id: 'delete',
        icon: 'bin',
        label: this.intl.t('custom_reporting.actions.delete'),
      },
    ] as const;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get exportOptions() {
    return [
      { id: 'csv', label: 'CSV' },
      { id: 'pdf', label: 'PDF' },
      { id: 'png', label: 'PNG' },
      { id: 'jpg', label: 'JPG' },
    ] as const;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get exportValue() {
    return {
      icon: 'export',
      label: this.intl.t('general.export'),
    } as unknown as (typeof this.exportOptions)[number];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get iframeQPs() {
    const embedDomain = ENV.isDevelopmentOrTest
      ? 'http://localhost:4200'
      : this.ajax.host;

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return {
      embed_domain: embedDomain,
      market_id: this.hotelAndMarketService.market?.id,
      timezone,
      user_type: 'custom_reporting',
    } as IFrameQueryParams;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEditRoute() {
    return (
      this.router.currentRouteName === 'market.custom-reporting.dashboard.edit'
    );
  }

  @action copyDashboard(): void {
    this.customReporting.copyDashboard(this.model as LookerDashboard);
  }

  @action editDashboard(): void {
    this.customReporting.triggerEdit();
  }

  @action handleExport(option: (typeof this.exportOptions)[number]): void {
    const hasFilters = Object.keys(this.currentFilters).length > 0;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const downloadURL = (this.model as LookerDashboard).buildDownloadURL(
      option.id,
      hasFilters
        ? {
            // remove emojis from filters
            filters: JSON.stringify(this.currentFilters).replace(
              /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
              '',
            ),
          }
        : undefined,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    openUrl(downloadURL);
  }

  @action onEditOptionSelect(option: (typeof this.editOptions)[number]): void {
    switch (option.id) {
      case 'schedule':
        this.customReporting.triggerSchedule();
        break;
      case 'reset-filters':
        this.customReporting.triggerDashboardFilters(this.initialFilters);
        break;
      case 'delete':
        this.customReporting.deleteDashboard(this.model as LookerDashboard);
        break;
      default:
        option satisfies never;
    }
  }

  @action onMessage(type: string, data: LookerMessageData): void {
    if (data.id !== this.customReporting.selectedDashboard?.id) {
      // Ignore messages for other dashboards
      return;
    }

    if (type === 'dashboard:loaded') {
      this.customReporting.dashboardLoading = false;
      this.canEdit = data.canEdit;
      this.isEmpty = Object.keys(data.options.elements).length === 0;
      this.currentFilters = data.dashboard_filters;
      this.initialFilters = data.dashboard_filters;
    }

    // When clicking on Cancel in Looker, redirect back to the main dashboard route
    if (
      type === 'dashboard:edit:cancel' ||
      type === 'dashboard:save:complete'
    ) {
      this.isEmpty = Object.keys(data.options.elements).length === 0;
      this.router.replaceWith('market.custom-reporting.dashboard', data.id);
    }

    // When clicking Edit in Looker, redirect to the edit route
    if (type === 'dashboard:edit:start') {
      this.router.replaceWith(
        'market.custom-reporting.dashboard.edit',
        data.id,
      );
    }

    if (
      type === 'dashboard:loaded' &&
      this.customReporting.pendingScheduleRequest
    ) {
      this.customReporting.triggerSchedule();
    }

    if (type === 'dashboard:run:complete') {
      // if the dashboard has data, we need to wait for the first run to finish
      if (
        this.router.currentRouteName ===
        'market.custom-reporting.dashboard.edit'
      ) {
        this.customReporting.triggerEdit();
      }

      this.isRefreshRunning = false;
      this.lastRunAt = dayjs();
      this.lastRunAtFormatted = this.lastRunAt.fromNow();

      this.intervalTimer = setInterval(() => {
        // eslint-disable-next-line ember/no-runloop
        next(() => {
          this.lastRunAtFormatted = this.lastRunAt?.fromNow();
        });
      }, 60000);
    }

    if (type === 'dashboard:filters:changed') {
      this.currentFilters = data.dashboard_filters;
    }
  }

  @action renameDashboard(): void {
    this.customReporting.editDashboard(this.model as LookerDashboard);
  }

  @action reset(): void {
    this.canEdit = false;
    this.isEmpty = true;
    this.currentFilters = {};
    this.initialFilters = {};
    this.lastRunAt = undefined;
    this.lastRunAtFormatted = undefined;
    clearInterval(this.intervalTimer);
  }

  @action triggerRefresh(): void {
    this.isRefreshRunning = true;
    this.customReporting.triggerDashboardRun();
  }
}
